import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/i18n/LangContext";
import { imagesToPdf } from "@/lib/imageToPdf";
import { setPendingFile } from "@/lib/fileBridge";
import { downloadBytes, uid } from "@/lib/pdfEditor";
import { ProgressBar, stageLabel } from "@/components/ProgressBar";
import { InfoSections } from "@/components/InfoSections";
import { GUIDES } from "@/content/seoContent";
import {
  ArrowLeft, Camera, ImagePlus, X, Loader2, Download, FileSpreadsheet,
  FileText, ChevronLeft, ChevronRight, Sparkles, RotateCcw,
} from "lucide-react";

const T = {
  it: { title: "Immagine → PDF", sub: "Trasforma istantaneamente le tue foto o scansioni in PDF pronti per la conversione. JPG e PNG, tutto nel tuo browser.", drop: "Trascina qui le tue immagini", dropHint: "JPG, PNG • Anche foto scattate col cellulare", addMore: "Aggiungi immagini", takePhoto: "Scatta foto", generate: "Genera PDF", generating: "Creazione PDF...", ready: "Il tuo PDF è pronto!", readySub: "Scaricalo oppure convertilo subito in Excel o Word.", download: "Scarica PDF", toExcel: "Converti in Excel", toWord: "Converti in Word", restart: "Nuove immagini", images: "immagini", image: "immagine", genErr: "Errore durante la creazione del PDF. Verifica che i file siano immagini valide.", badType: "Formato non valido: carica immagini JPG o PNG", moveLeft: "Sposta a sinistra", moveRight: "Sposta a destra", remove: "Rimuovi" },
  en: { title: "Image → PDF", sub: "Instantly turn your photos or scans into PDFs ready for conversion. JPG and PNG, all in your browser.", drop: "Drag your images here", dropHint: "JPG, PNG • Even photos taken with your phone", addMore: "Add images", takePhoto: "Take photo", generate: "Generate PDF", generating: "Creating PDF...", ready: "Your PDF is ready!", readySub: "Download it or convert it straight to Excel or Word.", download: "Download PDF", toExcel: "Convert to Excel", toWord: "Convert to Word", restart: "New images", images: "images", image: "image", genErr: "Error while creating the PDF. Check that the files are valid images.", badType: "Invalid format: upload JPG or PNG images", moveLeft: "Move left", moveRight: "Move right", remove: "Remove" },
  es: { title: "Imagen → PDF", sub: "Convierte al instante tus fotos o escaneos en PDF listos para la conversión. JPG y PNG, todo en tu navegador.", drop: "Arrastra aquí tus imágenes", dropHint: "JPG, PNG • Incluso fotos tomadas con el móvil", addMore: "Añadir imágenes", takePhoto: "Tomar foto", generate: "Generar PDF", generating: "Creando PDF...", ready: "¡Tu PDF está listo!", readySub: "Descárgalo o conviértelo directamente a Excel o Word.", download: "Descargar PDF", toExcel: "Convertir a Excel", toWord: "Convertir a Word", restart: "Nuevas imágenes", images: "imágenes", image: "imagen", genErr: "Error al crear el PDF. Comprueba que los archivos sean imágenes válidas.", badType: "Formato no válido: sube imágenes JPG o PNG", moveLeft: "Mover a la izquierda", moveRight: "Mover a la derecha", remove: "Eliminar" },
  fr: { title: "Image → PDF", sub: "Transformez instantanément vos photos ou scans en PDF prêts pour la conversion. JPG et PNG, tout dans votre navigateur.", drop: "Glissez vos images ici", dropHint: "JPG, PNG • Même des photos prises avec votre téléphone", addMore: "Ajouter des images", takePhoto: "Prendre une photo", generate: "Générer le PDF", generating: "Création du PDF...", ready: "Votre PDF est prêt !", readySub: "Téléchargez-le ou convertissez-le directement en Excel ou Word.", download: "Télécharger le PDF", toExcel: "Convertir en Excel", toWord: "Convertir en Word", restart: "Nouvelles images", images: "images", image: "image", genErr: "Erreur lors de la création du PDF. Vérifiez que les fichiers sont des images valides.", badType: "Format non valide : chargez des images JPG ou PNG", moveLeft: "Déplacer à gauche", moveRight: "Déplacer à droite", remove: "Supprimer" },
  de: { title: "Bild → PDF", sub: "Verwandeln Sie Ihre Fotos oder Scans sofort in konvertierungsbereite PDFs. JPG und PNG, alles in Ihrem Browser.", drop: "Ziehen Sie Ihre Bilder hierher", dropHint: "JPG, PNG • Auch Handyfotos", addMore: "Bilder hinzufügen", takePhoto: "Foto aufnehmen", generate: "PDF erstellen", generating: "PDF wird erstellt...", ready: "Ihr PDF ist fertig!", readySub: "Laden Sie es herunter oder konvertieren Sie es direkt in Excel oder Word.", download: "PDF herunterladen", toExcel: "In Excel umwandeln", toWord: "In Word umwandeln", restart: "Neue Bilder", images: "Bilder", image: "Bild", genErr: "Fehler beim Erstellen des PDFs. Prüfen Sie, ob die Dateien gültige Bilder sind.", badType: "Ungültiges Format: Laden Sie JPG- oder PNG-Bilder hoch", moveLeft: "Nach links", moveRight: "Nach rechts", remove: "Entfernen" },
};

export default function ImageToPdf() {
  const { lang, t: tGlobal } = useLang();
  const t = T[lang] || T.it;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [items, setItems] = useState([]); // { id, file, url }
  const [generating, setGenerating] = useState(false);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [genPct, setGenPct] = useState(0);
  const [drag, setDrag] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => () => itemsRef.current.forEach((i) => URL.revokeObjectURL(i.url)), []);

  const MAX_IMAGES = 40;

  const addFiles = (fileList) => {
    const accepted = [];
    for (const f of Array.from(fileList || [])) {
      if (f.type.startsWith("image/")) accepted.push({ id: uid("img"), file: f, url: URL.createObjectURL(f) });
      else toast.error(`${t.badType} (${f.name})`);
    }
    if (accepted.length) {
      setItems((prev) => {
        const next = [...prev, ...accepted];
        if (next.length > MAX_IMAGES) {
          toast.error(`Max ${MAX_IMAGES}`);
          return next.slice(0, MAX_IMAGES);
        }
        return next;
      });
      setPdfBytes(null);
    }
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const it = prev.find((i) => i.id === id);
      if (it) URL.revokeObjectURL(it.url);
      return prev.filter((i) => i.id !== id);
    });
    setPdfBytes(null);
  };

  const moveItem = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setPdfBytes(null);
  };

  const generate = async () => {
    if (!items.length) return;
    setGenerating(true);
    setGenPct(2);
    try {
      const bytes = await imagesToPdf(
        items.map((i) => i.file),
        (done, total) => setGenPct(Math.round((done / total) * 95)),
      );
      setGenPct(100);
      setPdfBytes(bytes);
    } catch (e) {
      console.error(e);
      toast.error(t.genErr);
    } finally {
      setGenerating(false);
    }
  };

  const pdfFile = () => new File([pdfBytes], "immagini.pdf", { type: "application/pdf" });

  const convertTo = (target) => {
    setPendingFile(pdfFile());
    navigate(target === "excel" ? "/pdf-excel" : "/pdf-word");
  };

  const reset = () => {
    items.forEach((i) => URL.revokeObjectURL(i.url));
    setItems([]);
    setPdfBytes(null);
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
      <div className="mb-6 flex justify-start">
        <Link to="/" data-testid="back-home-link" className="group inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-500 hover:text-white hover:shadow-md">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {T[lang]?.title ? (lang === "it" ? "Torna alla home" : lang === "en" ? "Back to home" : lang === "es" ? "Volver al inicio" : lang === "fr" ? "Retour à l'accueil" : "Zurück zur Startseite") : "Home"}
        </Link>
      </div>

      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
          <Camera className="h-7 w-7" />
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t.title}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">{t.sub}</p>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="image-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

        {items.length === 0 ? (
          <div
            data-testid="image-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            className={`group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-all ${drag ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-slate-50 hover:border-amber-400 hover:bg-amber-50/40"}`}
          >
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 transition-transform group-hover:scale-105">
              <ImagePlus className="h-8 w-8" />
            </div>
            <p className="font-display text-xl font-bold text-slate-900">{t.drop}</p>
            <p className="mt-4 text-xs font-medium text-slate-400">{t.dropHint}</p>
            <button
              data-testid="take-photo-button"
              onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
              className="mt-6 flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-amber-600"
            >
              <Camera className="h-4 w-4" /> {t.takePhoto}
            </button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="image-grid">
              {items.map((it, idx) => (
                <div key={it.id} className="group relative rounded-xl border border-slate-200 bg-slate-50 p-2" data-testid={`image-item-${idx}`}>
                  <button onClick={() => removeItem(it.id)} title={t.remove} data-testid={`image-remove-${idx}`} className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600"><X className="h-3.5 w-3.5" /></button>
                  <img src={it.url} alt={`img-${idx + 1}`} className="h-32 w-full rounded-lg object-contain" />
                  <div className="mt-1 flex items-center justify-between">
                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} title={t.moveLeft} data-testid={`image-left-${idx}`} className="rounded p-1 text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="text-xs font-bold text-slate-500">{idx + 1}</span>
                    <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} title={t.moveRight} data-testid={`image-right-${idx}`} className="rounded p-1 text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button data-testid="add-more-images" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                <ImagePlus className="h-4 w-4" /> {t.addMore}
              </button>
              <button data-testid="take-photo-button-2" onClick={() => cameraInputRef.current?.click()} className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                <Camera className="h-4 w-4" /> {t.takePhoto}
              </button>
              <span className="ml-auto text-sm font-medium text-slate-400">{items.length} {items.length === 1 ? t.image : t.images}</span>
            </div>

            {!pdfBytes && (
              <>
                <button
                  data-testid="generate-pdf-button"
                  onClick={generate}
                  disabled={generating}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-4 text-base font-bold text-white shadow-sm transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? (<><Loader2 className="h-5 w-5 animate-spin" /> {t.generating}</>) : (<><Sparkles className="h-5 w-5" /> {t.generate}</>)}
                </button>
                {generating && (
                  <div className="mt-4">
                    <ProgressBar progress={genPct} label={stageLabel(tGlobal, "generate")} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {pdfBytes && (
        <div data-testid="pdf-ready-panel" className="dotec-fade-up mt-8 rounded-3xl border border-emerald-200 bg-emerald-50/60 p-6 text-center shadow-sm sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-slate-900">{t.ready}</h2>
          <p className="mt-2 text-slate-500">{t.readySub}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="download-pdf-button" onClick={() => downloadBytes(pdfBytes, "immagini.pdf")} className="flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-slate-700">
              <Download className="h-4 w-4" /> {t.download}
            </button>
            <button data-testid="convert-to-excel-button" onClick={() => convertTo("excel")} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700">
              <FileSpreadsheet className="h-4 w-4" /> {t.toExcel}
            </button>
            <button data-testid="convert-to-word-button" onClick={() => convertTo("word")} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-700">
              <FileText className="h-4 w-4" /> {t.toWord}
            </button>
            <button data-testid="restart-images-button" onClick={reset} className="flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-white">
              <RotateCcw className="h-4 w-4" /> {t.restart}
            </button>
          </div>
        </div>
      )}

      {/* Tool guide (SEO / AdSense long-form content) */}
      <div className="mt-4 rounded-3xl border border-slate-100 bg-white/60">
        <InfoSections sections={GUIDES.image[lang] || GUIDES.image.en} testId="image-guide" />
      </div>
    </div>
  );
}
